import { type Dispatch, useContext, useEffect, useRef } from 'react'

import { type FrameBuilderState } from './state'
import { type FrameBuilderStateAction } from './state-mutation'
import {
  getIsArtworkImageValid,
  getIsArtworkSizeValid,
  getIsGlassValid,
  getIsPassepartoutColorValid,
  getIsPassepartoutSizeValid,
  getIsProfileValid,
  getIsTextureValid,
} from './validation/state-is-valid'
import { FrameBuilderConfigurationContext } from '../contexts/configuration-context'

/**
 * Manages automatic saving of selected state to committed state.
 */
export const useAutomaticCommittedStateUpdate = (
  selectedState: FrameBuilderState,
  dispatchCommittedState: Dispatch<FrameBuilderStateAction>,
) => {
  const configuration = useContext(FrameBuilderConfigurationContext)
  const isLoaded = useRef(false)

  useEffect(() => {
    if (!isLoaded.current) {
      return
    }

    const isArtworkSizeValid = getIsArtworkSizeValid(
      configuration,
      selectedState.artwork.width,
      selectedState.artwork.height,
    )

    if (!isArtworkSizeValid) {
      return
    }

    dispatchCommittedState({
      type: 'artworkSize',
      width: selectedState.artwork.width,
      height: selectedState.artwork.height,
    })
  }, [
    configuration,
    dispatchCommittedState,
    selectedState.artwork.height,
    selectedState.artwork.width,
  ])

  useEffect(() => {
    if (!isLoaded.current) {
      return
    }

    const isArtworkImageValid = getIsArtworkImageValid(
      selectedState.artwork.image,
    )

    if (!isArtworkImageValid) {
      return
    }

    dispatchCommittedState({
      type: 'artworkImage',
      url: selectedState.artwork.image?.url,
      name: selectedState.artwork.image?.name,
    })
  }, [dispatchCommittedState, selectedState.artwork.image])

  useEffect(() => {
    if (!isLoaded.current) {
      return
    }

    dispatchCommittedState({
      type: 'artworkImage',
      color: selectedState.artwork.backgroundColor?.current,
    })
  }, [dispatchCommittedState, selectedState.artwork.backgroundColor])

  useEffect(() => {
    if (!isLoaded.current) {
      return
    }

    const isProfileValid = getIsProfileValid(
      selectedState.artwork.width,
      selectedState.artwork.height,
      selectedState.profile,
    )
    const isTextureValid = getIsTextureValid(
      selectedState.profile,
      selectedState.texture,
    )

    if (!isProfileValid || !isTextureValid) {
      return
    }

    dispatchCommittedState({
      type: 'frameStyle',
      profile: selectedState.profile,
      texture: selectedState.texture,
    })
  }, [
    dispatchCommittedState,
    selectedState.artwork.height,
    selectedState.artwork.width,
    selectedState.profile,
    selectedState.texture,
  ])

  useEffect(() => {
    if (!isLoaded.current) {
      return
    }

    const isPassepartoutSizeValid = getIsPassepartoutSizeValid(
      selectedState.artwork.width,
      selectedState.artwork.height,
      selectedState.passepartout.size,
      selectedState.passepartout.color,
      selectedState.profile,
    )
    const isPassepartoutColorValid = getIsPassepartoutColorValid(
      selectedState.passepartout.size,
      selectedState.passepartout.color,
      selectedState.profile,
    )

    if (!isPassepartoutSizeValid || !isPassepartoutColorValid) {
      return
    }

    dispatchCommittedState({
      type: 'passepartout',
      size: selectedState.passepartout.size,
      color: selectedState.passepartout.color,
    })
  }, [
    dispatchCommittedState,
    selectedState.artwork.height,
    selectedState.artwork.width,
    selectedState.passepartout.color,
    selectedState.passepartout.size,
    selectedState.profile,
  ])

  useEffect(() => {
    if (!isLoaded.current) {
      return
    }

    const isGlassValid = getIsGlassValid(selectedState.glass)

    if (!isGlassValid) {
      return
    }

    dispatchCommittedState({
      type: 'glass',
      glass: selectedState.glass,
    })
  }, [dispatchCommittedState, selectedState.glass])

  useEffect(() => {
    if (!isLoaded.current) {
      return
    }

    dispatchCommittedState({
      type: 'extras',
      spacerSize: selectedState.extras.spacer.size,
      spacerColor: selectedState.extras.spacer.color,
      extraServices: selectedState.extras.extraServices,
    })
  }, [
    dispatchCommittedState,
    selectedState.extras.spacer.color,
    selectedState.extras.extraServices,
    selectedState.extras.spacer.size,
  ])

  useEffect(() => {
    isLoaded.current = true
  }, [])
}
